import React, { useState, useEffect } from 'react'
import {
  SunIcon,
  CloudRainIcon,
  UmbrellaIcon,
  SwimmingIcon,
  CloudIcon,
  WindIcon,
  CloudLightningIcon,
} from 'lucide-react'
import Header from '../components/Header'
// Mock weerdata (in een echte app zou dit van een API komen)
const generateWeatherData = () => {
  const today = new Date()
  // Simuleer verschillende weersomstandigheden
  const conditions = ['sunny', 'partly_cloudy', 'cloudy', 'rainy', 'stormy']
  const getRandomCondition = () =>
    conditions[Math.floor(Math.random() * conditions.length)]
  // Baseer temperatuur op seizoen (zomer voor een camping app)
  const getRandomTemp = () => Math.floor(Math.random() * 10) + 18 // 18-28°C
  // Genereer forecast voor 5 dagen
  const forecast = []
  for (let i = 0; i < 5; i++) {
    const date = new Date(today)
    date.setDate(today.getDate() + i)
    forecast.push({
      date,
      day:
        i === 0
          ? 'Vandaag'
          : i === 1
            ? 'Morgen'
            : new Intl.DateTimeFormat('nl-NL', { weekday: 'short' }).format(
                date,
              ),
      temp: getRandomTemp(),
      condition: getRandomCondition(),
      wind: Math.floor(Math.random() * 20) + 5,
      rainChance: Math.floor(Math.random() * 100), // 0-100%
    })
  }
  return {
    current: forecast[0],
    forecast: forecast.slice(1),
  }
}
const Weather = () => {
  const [weatherData, setWeatherData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  useEffect(() => {
    // Simuleer API call
    const fetchWeather = async () => {
      setIsLoading(true)
      // Wacht even om een echte API call te simuleren
      await new Promise((resolve) => setTimeout(resolve, 1000))
      setWeatherData(generateWeatherData())
      setIsLoading(false)
    }
    fetchWeather()
  }, [])
  const getWeatherIcon = (condition, size = 24) => {
    switch (condition) {
      case 'sunny':
        return <SunIcon size={size} className="text-yellow-500" />
      case 'partly_cloudy':
        return <CloudIcon size={size} className="text-gray-400" />
      case 'cloudy':
        return <CloudIcon size={size} className="text-gray-500" />
      case 'rainy':
        return <CloudRainIcon size={size} className="text-blue-500" />
      case 'stormy':
        return <CloudLightningIcon size={size} className="text-purple-500" />
      default:
        return <SunIcon size={size} className="text-yellow-500" />
    }
  }
  const getActivityTip = (weather) => {
    if (!weather) return null
    if (weather.condition === 'stormy') {
      return {
        icon: <CloudLightningIcon size={20} />,
        text: 'Onweer op komst! Blijf binnen en geniet van onze gameroom of restaurant.',
      }
    } else if (weather.condition === 'rainy') {
      return {
        icon: <UmbrellaIcon size={20} />,
        text: `Regenachtig weer (${weather.rainChance}% kans). Bezoek onze overdekte faciliteiten of neem deel aan indoor activiteiten.`,
      }
    } else if (weather.temp >= 25) {
      return {
        icon: <SwimmingIcon size={20} />,
        text: `Heerlijk weer (${weather.temp}°C)! Perfect voor het zwembad of een kanotocht op de Amblève.`,
      }
    } else if (weather.temp >= 20 && weather.condition !== 'rainy') {
      return {
        icon: <HikingIcon size={20} />,
        text: `Aangenaam weer (${weather.temp}°C). Ideaal voor een mountainbiketocht of wandeling in de Ardennen.`,
      }
    } else {
      return {
        icon: <CampfireIcon size={20} />,
        text: `Mild weer (${weather.temp}°C). Goed voor bushcraft activiteiten of een gezellig kampvuur.`,
      }
    }
  }
  const getWindDescription = (speed) => {
    if (speed < 10) return 'Rustig'
    if (speed < 20) return 'Matig'
    return 'Krachtig'
  }
  const getRainDescription = (chance) => {
    if (chance < 20) return 'Zeer kleine kans'
    if (chance < 40) return 'Kleine kans'
    if (chance < 60) return 'Kans op regen'
    if (chance < 80) return 'Grote kans'
    return 'Zeer grote kans'
  }
  if (isLoading) {
    return (
      <div className="flex flex-col min-h-full bg-gray-50">
        <Header title="Weer & Tips" showBackButton />
        <div className="p-4 flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#f9b30f] mx-auto mb-4"></div>
            <p className="text-gray-600">Weergegevens laden...</p>
          </div>
        </div>
      </div>
    )
  }
  const tip = weatherData ? getActivityTip(weatherData.current) : null
  return (
    <div className="flex flex-col min-h-full bg-gray-50">
      <Header title="Weer & Tips" showBackButton />
      <div className="p-4">
        {weatherData && (
          <>
            <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
              <h2 className="text-lg font-bold mb-2">Actueel Weer</h2>
              <div className="flex items-center mb-4">
                <div className="mr-4">
                  {getWeatherIcon(weatherData.current.condition, 48)}
                </div>
                <div>
                  <span className="text-3xl font-bold">
                    {weatherData.current.temp}°C
                  </span>
                  <div className="text-sm text-gray-600 mt-1">
                    {weatherData.current.condition === 'sunny'
                      ? 'Zonnig'
                      : weatherData.current.condition === 'partly_cloudy'
                        ? 'Licht bewolkt'
                        : weatherData.current.condition === 'cloudy'
                          ? 'Bewolkt'
                          : weatherData.current.condition === 'rainy'
                            ? 'Regenachtig'
                            : 'Onweerachtig'}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center">
                  <WindIcon size={18} className="text-gray-500 mr-2" />
                  <div>
                    <div className="text-sm font-medium">Wind</div>
                    <div className="text-sm text-gray-600">
                      {weatherData.current.wind} km/u (
                      {getWindDescription(weatherData.current.wind)})
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  <CloudRainIcon size={18} className="text-gray-500 mr-2" />
                  <div>
                    <div className="text-sm font-medium">Neerslag</div>
                    <div className="text-sm text-gray-600">
                      {weatherData.current.rainChance}% (
                      {getRainDescription(weatherData.current.rainChance)})
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 mb-4 overflow-x-auto">
              <h2 className="text-lg font-bold mb-3">Voorspelling</h2>
              <div className="flex space-x-6 min-w-max">
                {[weatherData.current, ...weatherData.forecast].map(
                  (day, index) => (
                    <div key={index} className="text-center min-w-[70px]">
                      <div className="text-sm font-medium mb-2">{day.day}</div>
                      <div className="mb-2">
                        {getWeatherIcon(day.condition)}
                      </div>
                      <div className="text-lg font-bold">{day.temp}°C</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {day.rainChance}% regen
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
            <div className="bg-gradient-to-r from-[#f9b30f]/20 to-[#f9b30f]/5 rounded-lg p-4">
              <h2 className="text-lg font-bold mb-2">Activiteitentip</h2>
              <div className="flex items-start">
                <div className="text-[#f9b30f] mr-3 mt-0.5">{tip?.icon}</div>
                <p className="text-sm">{tip?.text}</p>
              </div>
              <div className="mt-4 text-sm text-gray-600">
                <p className="font-medium">Beste activiteiten voor vandaag:</p>
                <ul className="mt-1 space-y-1">
                  {weatherData.current.condition === 'sunny' &&
                  weatherData.current.temp >= 25 ? (
                    <>
                      <li className="flex items-center">
                        <span className="w-1.5 h-1.5 bg-[#f9b30f] rounded-full mr-2"></span>
                        Zwemmen in het zwembad
                      </li>
                      <li className="flex items-center">
                        <span className="w-1.5 h-1.5 bg-[#f9b30f] rounded-full mr-2"></span>
                        Kanotocht op de Amblève
                      </li>
                      <li className="flex items-center">
                        <span className="w-1.5 h-1.5 bg-[#f9b30f] rounded-full mr-2"></span>
                        Vlottenbouwen bij de rivier
                      </li>
                    </>
                  ) : weatherData.current.condition === 'rainy' ||
                    weatherData.current.condition === 'stormy' ? (
                    <>
                      <li className="flex items-center">
                        <span className="w-1.5 h-1.5 bg-[#f9b30f] rounded-full mr-2"></span>
                        Bordspellen in de gameroom
                      </li>
                      <li className="flex items-center">
                        <span className="w-1.5 h-1.5 bg-[#f9b30f] rounded-full mr-2"></span>
                        Restaurant & bar bezoeken
                      </li>
                      <li className="flex items-center">
                        <span className="w-1.5 h-1.5 bg-[#f9b30f] rounded-full mr-2"></span>
                        Indoor knutselactiviteiten
                      </li>
                    </>
                  ) : (
                    <>
                      <li className="flex items-center">
                        <span className="w-1.5 h-1.5 bg-[#f9b30f] rounded-full mr-2"></span>
                        Mountainbiken in de omgeving
                      </li>
                      <li className="flex items-center">
                        <span className="w-1.5 h-1.5 bg-[#f9b30f] rounded-full mr-2"></span>
                        Wandelen in de Ardennen
                      </li>
                      <li className="flex items-center">
                        <span className="w-1.5 h-1.5 bg-[#f9b30f] rounded-full mr-2"></span>
                        Bushcraft workshop
                      </li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
const HikingIcon = ({ size }) => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 22h-4l-2-8 6-3-1-4 3-2"></path>
      <path d="M14 13l-6 3"></path>
      <circle cx="7" cy="5" r="2"></circle>
      <path d="M22 13l-3 3-3-2-2 4h-5"></path>
    </svg>
  )
}
const CampfireIcon = ({ size }) => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M8 18c0 2.21 2.69 4 6 4s6-1.79 6-4c0-4-3-6-6-10-3 4-6 6-6 10z"></path>
      <path d="M12 15c-1 0-3 .5-3 2s2 2 3 2 3-.5 3-2-2-2-3-2z"></path>
      <path d="M8.5 8.5L7 7"></path>
      <path d="M15.5 8.5L17 7"></path>
    </svg>
  )
}
const SwimmingIcon = ({ size }) => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 12h1a7 7 0 0 1 7 7h0a7 7 0 0 1 7-7h1M10.67 5.5l2.66 2.66M14.5 2l-5 5 2.66 2.66"></path>
    </svg>
  )
}
export default Weather
